import {
  FC,
  createElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useWebgate } from '@xvia/webgate-connect';
import { getServerData } from '../../util/get-server-data';
import { useHistory, useParams } from 'react-router-dom';
import {
  getDepartments,
  getSection,
  IDepartment,
  ISection,
} from '../../apis/department';
import { Department, DepartmentProps } from './department';
import { Catalog, search } from '../../apis/search-catalog';
import { BreadcrumbItem } from '../../components/breadcrumb/breadcrumb';
import { ROUTES } from '../../routes';
import { CatalogDetail } from '../../apis/detail-catalog';
import { message } from 'antd';
import { buildAppPath } from '../../utils/basePath';

type DepartmentSection = CatalogDetail['departmentSections'];

export const DepartmentContainer: FC = () => {
  const { serverData } = useWebgate();
  const history = useHistory();
  const { orgao } = useParams<{ orgao: string }>();
  const ssrData =
    serverData?.departmentList || getServerData('departmentList', null);

  const [department, setDepartment] = useState<IDepartment | undefined>(
    ssrData?.find((i: IDepartment) => i.slug === orgao)
  );
  const [departmentSections, setDepartmentSections] = useState<DepartmentSection>();
  const [catalogList, setCatalogList] = useState<Catalog[]>();
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const departmentServicesRef = useRef<HTMLDivElement>();

  const serverSearch = useCallback((departmentSlug: string) => {
    search({ departmentSlug: [departmentSlug], from: 0, size: 12 })
      .then((res) => {
        setCatalogList(res);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const mapSections = useCallback(
    (sections: ISection[] | undefined) => {
      if (!sections || !department) {
        setDepartmentSections(undefined);
        return;
      }

      const departmentInfo = {
        id: department.id,
        slug: department.slug,
        name: department.name,
        shortName: department.shortName,
        mission: department.mission,
        vision: department.vision,
        values: department.values,
        hidden: department.hidden,
        active: department.active,
        description: department.description,
      };

      const mappedSections = sections.map((section) => ({
        ...section,
        department: departmentInfo,
      }));

      setDepartmentSections(mappedSections as DepartmentSection);
    },
    [department]
  );

  const getDepartment = useCallback(() => {
    setLoading(true);
    setNotFound(false);
    getDepartments()
      .then((list) => {
        const departmentFound = list?.find((i) => i.slug === orgao);

        if (!departmentFound) {
          setNotFound(true);
          return;
        }

        setDepartment(departmentFound);
      })
      .catch((err) => {
        console.log(err);
        message.error('Não foi possível carregar as informações do órgão.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [orgao]);

  const getSectionServer = useCallback(
    (departmentId?: string) => {
      if (!departmentId) return;

      getSection(departmentId)
        .then(mapSections)
        .catch((err) => {
          console.log(err);
          message.error('Não foi possível carregar as unidades deste órgão.');
        });
    },
    [mapSections]
  );

  const onClickCatalog = useCallback(
    (slug) => {
      history.push(`${ROUTES.SERVICE_LIST_PAGE}/${slug}`);
    },
    [history]
  );

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    // Reset state when slug changes to avoid showing stale data
    setDepartment(undefined);
    setDepartmentSections(undefined);
    setCatalogList(undefined);
    setBreadcrumbs([]);
    getDepartment();
    serverSearch(orgao);
  }, [orgao, getDepartment, serverSearch]);

  useEffect(() => {
    if (department) {
      getSectionServer(department.id);
    }
  }, [department, getSectionServer]);

  useEffect(() => {
    if (notFound) {
      message.error('Órgão não encontrado.');
      history.replace(buildAppPath(ROUTES.DEPARTMENT_LIST_PAGE));
    }
  }, [history, notFound]);

  useEffect(() => {
    if (!department) {
      return;
    }

    setBreadcrumbs([
      { name: 'Início', path: buildAppPath(ROUTES.HOME_PAGE) },
      {
        name: 'Órgãos e secretarias',
        path: buildAppPath(ROUTES.DEPARTMENT_LIST_PAGE),
      },
      { name: department.name },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [department]);

  const props: DepartmentProps = {
    ...department,
    departmentSections,
    catalogList,
    onClickCatalog,
    departmentServicesRef,
    breadcrumbs,
  } as DepartmentProps;

  if (loading) {
    return null;
  }

  if (notFound) {
    return null;
  }

  return department ? createElement(Department, props) : null;
};
